import React, { useEffect, useState } from "react";
import "../../styles/clients.css";
import { Plus, Edit, Trash2, RefreshCw, X } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { confirmAndDelete } from "../../utils/deleteHelper";

const EMPTY_FORM = {
    complaint_id: null,
    complaint_name: "",
};

const Complaint = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);

    const {
        data: complaints = [],
        loading,
        fetchAll: fetchComplaints,
        save: saveComplaint,
        remove: deleteComplaint,
    } = useGeneric({
        tableName: "complaintmaster",
        idColumn: "complaint_id",
        orderBy: "complaint_name",
        removeOnInsert: ["complaint_id"],
    });

    useEffect(() => {
        fetchComplaints();
    }, [fetchComplaints]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    // ── Validation ───────────────────────────────────────────────────
    const validate = () => {
        const errs = {};

        if (!(form.complaint_name ?? "").trim())
            errs.complaint_name = "Complaint name is required.";
        else if (form.complaint_name.length > 50)
            errs.complaint_name = "Max 50 characters.";

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Save ─────────────────────────────────────────────────────────
    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            await saveComplaint(form);
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            alert("Save failed: " + (err.message || "Unknown error"));
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        setShowForm(true);
    };

    const openEdit = (complaint) => {
        setForm({ ...complaint }); // ✅ fixed: spread the selected complaint, not the whole array
        setErrors({});
        setShowForm(true);
    };

    const closeModal = () => {
        setShowForm(false);
        setForm(EMPTY_FORM);
        setErrors({});
    };

    // ── Delete ───────────────────────────────────────────────────────
    const handleDelete = (row, e) => {
        e?.stopPropagation();
        confirmAndDelete({
            what: "complaint",
            name: row.complaint_name,
            run: () => deleteComplaint(row.complaint_id),
        });
    };

    // ── Filter ───────────────────────────────────────────────────────
    const filtered = (complaints || [])
        .filter((u) =>
            u.complaint_name?.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => a.complaint_name?.localeCompare(b.complaint_name));

    return (
        <div className="cl-page">
            {/* ── Panel ── */}
            <div className="cl-panel">
                <div className="cl-panel-heading">
                    <span>Complaint Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add Complaint">
                        <Plus size={16} />
                    </button>
                </div>

                {/* Search bar */}
                <div className="cl-search-bar">
                    <input
                        type="text"
                        placeholder="Search complaints…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="cl-search-input"
                    />
                    <button className="cl-refresh-btn" onClick={fetchComplaints} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* Table */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <div className="cl-table-wrapper">
                        <table className="cl-table">
                            <thead>
                                <tr>
                                    <th>Complaint ID</th>
                                    <th>Complaint Name</th>
                                    <th className="cl-actions-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={3} className="cl-empty">
                                            No complaints found.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((u, i) => (
                                        <tr
                                            key={u.complaint_id ?? i}
                                            className={`${i % 2 === 0 ? "cl-row-even" : "cl-row-odd"} cl-row-clickable`}
                                            onClick={() => openEdit(u)}
                                            title="Click to edit"
                                        >
                                            <td data-label="Complaint ID">{u.complaint_id}</td>
                                            <td data-label="Complaint">{u.complaint_name}</td>
                                            <td className="cl-actions-td" onClick={(e) => e.stopPropagation()}>
                                                <button
                                                    className="cl-icon-btn cl-edit"
                                                    onClick={() => openEdit(u)}
                                                    title="Edit"
                                                >
                                                    <Edit size={15} /><span className="cl-icon-label">Edit</span>
                                                </button>
                                                <button
                                                    className="cl-icon-btn cl-delete"
                                                    onClick={(e) => handleDelete(u, e)}
                                                    title="Delete"
                                                >
                                                    <Trash2 size={15} /><span className="cl-icon-label">Delete</span>
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>

                        {/* Modal Header */}
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">
                                {form.complaint_id ? "Edit Complaint" : "Add Complaint"}
                            </h4>
                            <button className="cl-modal-close" onClick={closeModal}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="cl-modal-body">
                            <form autoComplete="off">
                                <div className="cl-form-group cl-row">
                                    <div className="cl-col-10">
                                        <label className="cl-control-label">Complaint Name</label>
                                        <input
                                            type="text"
                                            className={`cl-form-control${errors.complaint_name ? " cl-input-error" : ""}`}
                                            placeholder="Complaint Name"
                                            maxLength={50}
                                            value={form.complaint_name}
                                            onChange={(e) => set("complaint_name", e.target.value)}
                                        />
                                        {errors.complaint_name && (
                                            <span className="cl-error-msg">{errors.complaint_name}</span>
                                        )}
                                    </div>
                                    <div className="cl-col-2">
                                        <label className="cl-control-label">Complaint ID</label>
                                        <input
                                            type="text"
                                            className="cl-form-control cl-readonly"
                                            placeholder="Auto"
                                            value={form.complaint_id ?? ""}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </form>
                        </div>

                        {/* Modal Footer */}
                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>
                                Close
                            </button>
                            <button
                                className="cl-btn cl-btn-primary"
                                onClick={onSave}
                                disabled={saving}
                            >
                                {saving ? "Saving…" : form.complaint_id ? "Update" : "Add"}
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
};

export default Complaint;